import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { SUPPORTED_LOCALES } from '../i18n/config';
import { switchLocalePath } from '../i18n/routes';
import { cloudinaryOgImage } from '../utils/cloudinary';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  type?: string;
  noindex?: boolean;
}

const SITE_ORIGIN = 'https://www.camilaparente.store';

const OG_LOCALE_MAP: Record<string, string> = {
  en: 'en_US',
  it: 'it_IT',
  es: 'es_ES',
  fr: 'fr_FR',
  de: 'de_DE',
};

/** Ensure the image URL is absolute and optimized for social previews */
const resolveOgImage = (image: string): string => {
  // Already an absolute Cloudinary URL — optimize for OG
  if (image.includes('res.cloudinary.com')) {
    return cloudinaryOgImage(image);
  }
  // Relative path (e.g. /og-image.jpg) — make absolute
  if (image.startsWith('/')) {
    return `${SITE_ORIGIN}${image}`;
  }
  return image;
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image = '/og-image.jpg',
  type = 'website',
  noindex = false
}) => {
  const siteName = "Jewelry by Camila Parente";
  const { locale } = useLocale();
  const { pathname } = useLocation();

  const ogImage = resolveOgImage(image);

  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={locale} />

      {/* Basic metadata */}
      <title>{`${title} | ${siteName}`}</title>
      <meta name="description" content={description} />

      {/* Robots meta tag */}
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />

      {/* Canonical URL */}
      <link rel="canonical" href={`${SITE_ORIGIN}${pathname}`} />

      {/* Hreflang alternate links */}
      {SUPPORTED_LOCALES.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={`${SITE_ORIGIN}${switchLocalePath(pathname, loc)}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}${switchLocalePath(pathname, 'en')}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="1200" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={OG_LOCALE_MAP[locale] || 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default SEO;
